import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import { readJsonWithOnboarding } from '../onboarding/notify-onboarding-update';
import { Loader2 } from 'lucide-react';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import {
  ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME,
  EntityDetailModalLayout,
  EntityDetailModalTitle,
} from './EntityDetailModalLayout';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { DatePicker } from './DatePicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { RichTextField, type MentionableEntity } from './RichTextField';
import { RelatedEntityLink } from './RelatedEntityLink';
import { removeMentionFromHtml } from '../utils/entity-utils';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import { buildTimelineEventIso } from '../utils/timeline-event-iso';
import {
  formatTimeZoneLabel,
  getUserTimeZone,
  listTimeZones,
  precisionIncludesTime,
  resolveTimelineTimeZone,
  utcIsoToZonedParts,
} from '../utils/timeline-timezone';
import { useToast } from '../contexts/ToastContext';
import { ProjectTagsField } from './ProjectTagsField';
import { InlineEditableText } from './InlineEditableText';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { SingleSelectCombobox } from './ui/single-select-combobox';

export type PrecisionOption = 'year' | 'month' | 'day' | 'second';

export type TimelineItemTypeValue = 'historical_event' | 'fictional_event' | 'planning' | 'other';

export const TIMELINE_ITEM_TYPE_VALUES: TimelineItemTypeValue[] = [
  'historical_event',
  'fictional_event',
  'planning',
  'other',
];

export interface TimelineItemForEdit {
  id: string;
  name: string;
  description: string | null;
  item_type: TimelineItemTypeValue;
  tags: string[];
  start_date: string;
  start_precision: string;
  start_timezone?: string | null;
  end_date: string | null;
  end_precision: string | null;
  end_timezone?: string | null;
}

type DefaultsForNewItem = Pick<
  TimelineItemForEdit,
  | 'item_type'
  | 'tags'
  | 'start_date'
  | 'start_precision'
  | 'start_timezone'
  | 'end_date'
  | 'end_precision'
  | 'end_timezone'
> & { name?: string; description?: string | null };

interface AddTimelineEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onSuccess: (created?: { id: string; name: string }) => void;
  initialItem?: TimelineItemForEdit | null;
  defaultsForNewItem?: DefaultsForNewItem | null;
  scrollToSection?: 'related_entities';
}

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function TimelineYearInput({
  value,
  onChange,
  disabled,
  className,
}: {
  value: number;
  onChange: (year: number) => void;
  disabled: boolean;
  className?: string;
}): React.JSX.Element {
  return (
    <Input
      type="number"
      value={value}
      onChange={(e) => onChange(parseYearInput(e.target.value, value))}
      disabled={disabled}
      className={className}
    />
  );
}

function precisionToApiValue(p: PrecisionOption): 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' {
  return p === 'second' ? 'second' : p;
}

function parseYearInput(raw: string, fallback: number): number {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseDateString(s: string): { year: number; month: number; day: number } {
  const [y, m, d] = s.split('-').map(Number);
  return { year: y || new Date().getFullYear(), month: m || 1, day: d || 1 };
}

function precisionFromApi(p: string): PrecisionOption {
  if (p === 'year' || p === 'month' || p === 'day') return p;
  return 'second';
}

function normalizeRichTextDescription(html: string | undefined): string | null {
  const s = (html ?? '').trim();
  if (!s) return null;
  const emptyTags = ['<p></p>', '<p><br></p>', '<p><br/></p>'];
  if (emptyTags.includes(s)) return null;
  return s;
}

function partsToDateStr(year: number, month: number, day: number): string {
  return (
    year +
    '-' +
    String(month).padStart(2, '0') +
    '-' +
    String(day).padStart(2, '0')
  );
}

function partsToTimeStr(hour: number, minute: number): string {
  return String(hour).padStart(2, '0') + ':' + String(minute).padStart(2, '0');
}

function formStateFromItem(item: TimelineItemForEdit): {
  name: string;
  description: string;
  itemType: TimelineItemTypeValue;
  tags: string[];
  startPrecision: PrecisionOption;
  startYear: number;
  startMonth: number;
  startDateStr: string;
  startTimeStr: string;
  startTimeZone: string;
  hasEnd: boolean;
  endPrecision: PrecisionOption;
  endYear: number;
  endMonth: number;
  endDateStr: string;
  endTimeStr: string;
  endTimeZone: string;
} {
  const itemType: TimelineItemTypeValue = TIMELINE_ITEM_TYPE_VALUES.includes(
    item.item_type as TimelineItemTypeValue
  )
    ? (item.item_type as TimelineItemTypeValue)
    : 'other';
  const startPrecision = precisionFromApi(item.start_precision);
  const startTimeZone = precisionIncludesTime(startPrecision)
    ? resolveTimelineTimeZone(item.start_timezone)
    : getUserTimeZone();
  const startParts = precisionIncludesTime(startPrecision)
    ? utcIsoToZonedParts(item.start_date, startTimeZone)
    : utcIsoToZonedParts(item.start_date, 'UTC');
  const startDateStr = partsToDateStr(startParts.year, startParts.month, startParts.day);
  const startTimeStr = partsToTimeStr(startParts.hour, startParts.minute);
  const hasEnd = item.end_date != null && item.end_date !== '';
  let endPrecision: PrecisionOption = 'day';
  let endYear = startParts.year;
  let endMonth = startParts.month;
  let endDateStr = startDateStr;
  let endTimeStr = '12:00';
  let endTimeZone = startTimeZone;
  if (hasEnd && item.end_date) {
    endPrecision = precisionFromApi(item.end_precision || 'day');
    endTimeZone = precisionIncludesTime(endPrecision)
      ? resolveTimelineTimeZone(item.end_timezone ?? item.start_timezone)
      : startTimeZone;
    const endParts = precisionIncludesTime(endPrecision)
      ? utcIsoToZonedParts(item.end_date, endTimeZone)
      : utcIsoToZonedParts(item.end_date, 'UTC');
    endYear = endParts.year;
    endMonth = endParts.month;
    endDateStr = partsToDateStr(endParts.year, endParts.month, endParts.day);
    endTimeStr = partsToTimeStr(endParts.hour, endParts.minute);
  }
  return {
    name: item.name,
    description: item.description ?? '',
    itemType,
    tags: Array.isArray(item.tags) ? item.tags : [],
    startPrecision,
    startYear: startParts.year,
    startMonth: startParts.month,
    startDateStr,
    startTimeStr,
    startTimeZone,
    hasEnd,
    endPrecision,
    endYear,
    endMonth,
    endDateStr,
    endTimeStr,
    endTimeZone,
  };
}

export const AddTimelineEventModal: React.FC<AddTimelineEventModalProps> = ({
  isOpen,
  onClose,
  projectId,
  onSuccess,
  initialItem = null,
  defaultsForNewItem = null,
  scrollToSection,
}) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const relatedEntitiesSectionRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [itemType, setItemType] = useState<TimelineItemTypeValue>('other');
  const [tags, setTags] = useState<string[]>([]);
  const [startPrecision, setStartPrecision] = useState<PrecisionOption>('day');
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [startMonth, setStartMonth] = useState(new Date().getMonth() + 1);
  const [startDateStr, setStartDateStr] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });
  const [startTimeStr, setStartTimeStr] = useState('12:00');
  const [startTimeZone, setStartTimeZone] = useState(getUserTimeZone);
  const [hasEnd, setHasEnd] = useState(false);
  const [endPrecision, setEndPrecision] = useState<PrecisionOption>('day');
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(new Date().getMonth() + 1);
  const [endDateStr, setEndDateStr] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });
  const [endTimeStr, setEndTimeStr] = useState('12:00');
  const [endTimeZone, setEndTimeZone] = useState(getUserTimeZone);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [entityRefs, setEntityRefs] = useState<Array<{
    sourceType: string;
    sourceId: string;
    sourceDocumentId: string | null;
    targetType: string;
    targetId: string;
    targetDocumentId: string | null;
  }>>([]);
  const [entityRefsRefreshTrigger, setEntityRefsRefreshTrigger] = useState(0);
  const { showErrorToast } = useToast();
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const projectIdForLinks = projectId ?? routeProjectId ?? '';

  const timeZoneOptions = useMemo(() => {
    const userZone = getUserTimeZone();
    const zones = listTimeZones();
    const ordered = [
      userZone,
      ...zones.filter((zone) => zone !== userZone).sort((a, b) => a.localeCompare(b)),
    ];
    return ordered.map((zone) => ({
      value: zone,
      label: formatTimeZoneLabel(zone),
    }));
  }, []);

  useEffect(() => {
    const fetchMentionableEntities = async (): Promise<void> => {
      if (!projectId || !token || !isOpen) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };
    fetchMentionableEntities();
  }, [projectId, token, isOpen]);

  useEffect(() => {
    const fetchEntityRefs = async (): Promise<void> => {
      if (!projectId || !token || !isOpen) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setEntityRefs(data.references ?? []);
        } else {
          setEntityRefs([]);
        }
      } catch {
        setEntityRefs([]);
      }
    };
    fetchEntityRefs();
  }, [projectId, token, isOpen, entityRefsRefreshTrigger]);

  const linkedEntities = useMemo(() => {
    if (!initialItem) return { linksTo: [], referencedIn: [] };
    const entityLookup = new Map<string, MentionableEntity>();
    for (const e of mentionableEntities) {
      const key = `${e.type}:${e.id}:${e.documentId ?? ''}`;
      entityLookup.set(key, e);
    }
    const linksTo: Array<{ id: string; type: string; label: string; documentId?: string }> = [];
    const referencedIn: Array<{ id: string; type: string; label: string; documentId?: string }> = [];
    for (const ref of entityRefs) {
      if (ref.sourceType === 'timeline_item' && ref.sourceId === initialItem.id) {
        const key = `${ref.targetType}:${ref.targetId}:${ref.targetDocumentId ?? ''}`;
        const entity = entityLookup.get(key);
        if (entity) {
          linksTo.push({
            id: ref.targetId,
            type: ref.targetType,
            label: entity.label,
            documentId: ref.targetDocumentId ?? undefined,
          });
        }
      }
      if (ref.targetType === 'timeline_item' && ref.targetId === initialItem.id) {
        const key = `${ref.sourceType}:${ref.sourceId}:${ref.sourceDocumentId ?? ''}`;
        const entity = entityLookup.get(key);
        if (entity) {
          referencedIn.push({
            id: ref.sourceId,
            type: ref.sourceType,
            label: entity.label,
            documentId: ref.sourceDocumentId ?? undefined,
          });
        }
      }
    }
    const dedupe = (arr: typeof linksTo) => {
      const seen = new Set<string>();
      return arr.filter((x) => {
        const k = `${x.type}:${x.id}:${x.documentId ?? ''}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };
    return { linksTo: dedupe(linksTo), referencedIn: dedupe(referencedIn) };
  }, [initialItem, entityRefs, mentionableEntities]);

  useEffect(() => {
    if (!isOpen || scrollToSection !== 'related_entities') {
      return;
    }
    if (linkedEntities.linksTo.length === 0 && linkedEntities.referencedIn.length === 0) {
      return;
    }
    const frameId = window.requestAnimationFrame(() => {
      relatedEntitiesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [isOpen, scrollToSection, linkedEntities]);

  const handleRemoveLinkedEntity = useCallback(
    async (entity: { type: string; id: string; documentId?: string }): Promise<void> => {
      if (!initialItem || !projectId || !token) return;
      const newDesc = removeMentionFromHtml(description || '', entity);
      try {
        const res = await fetch(
          `${API_BASE}/api/projects/${projectId}/timeline-items/${initialItem.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ description: newDesc.trim() || null }),
          }
        );
        if (!res.ok) throw new Error('Failed to update');
        setDescription(newDesc.trim() || '');
        setEntityRefsRefreshTrigger((p) => p + 1);
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [initialItem, projectId, token, description, t, showErrorToast]
  );

  useEffect(() => {
    if (isOpen) {
      if (initialItem) {
        const state = formStateFromItem(initialItem);
        setName(state.name);
        setDescription(state.description);
        setItemType(state.itemType);
        setTags(state.tags);
        setStartPrecision(state.startPrecision);
        setStartYear(state.startYear);
        setStartMonth(state.startMonth);
        setStartDateStr(state.startDateStr);
        setStartTimeStr(state.startTimeStr);
        setStartTimeZone(state.startTimeZone);
        setHasEnd(state.hasEnd);
        setEndPrecision(state.endPrecision);
        setEndYear(state.endYear);
        setEndMonth(state.endMonth);
        setEndDateStr(state.endDateStr);
        setEndTimeStr(state.endTimeStr);
        setEndTimeZone(state.endTimeZone);
      } else if (defaultsForNewItem) {
        const syntheticItem: TimelineItemForEdit = {
          id: '',
          name: defaultsForNewItem.name ?? '',
          description: defaultsForNewItem.description ?? null,
          ...defaultsForNewItem,
        };
        const state = formStateFromItem(syntheticItem);
        setName(state.name);
        setDescription(state.description ?? '');
        setItemType(state.itemType);
        setTags(state.tags);
        setStartPrecision(state.startPrecision);
        setStartYear(state.startYear);
        setStartMonth(state.startMonth);
        setStartDateStr(state.startDateStr);
        setStartTimeStr(state.startTimeStr);
        setStartTimeZone(
          precisionIncludesTime(state.startPrecision) ? state.startTimeZone : getUserTimeZone()
        );
        setHasEnd(state.hasEnd);
        setEndPrecision(state.endPrecision);
        setEndYear(state.endYear);
        setEndMonth(state.endMonth);
        setEndDateStr(state.endDateStr);
        setEndTimeStr(state.endTimeStr);
        setEndTimeZone(
          precisionIncludesTime(state.endPrecision) ? state.endTimeZone : getUserTimeZone()
        );
      } else {
        setName('');
        setDescription('');
        setItemType('other');
        setTags([]);
        setStartPrecision('day');
        const d = new Date();
        const userZone = getUserTimeZone();
        setStartYear(d.getFullYear());
        setStartMonth(d.getMonth() + 1);
        setStartDateStr(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'));
        setStartTimeStr('12:00');
        setStartTimeZone(userZone);
        setHasEnd(false);
        setEndPrecision('day');
        setEndYear(d.getFullYear());
        setEndMonth(d.getMonth() + 1);
        setEndDateStr(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'));
        setEndTimeStr('12:00');
        setEndTimeZone(userZone);
      }
      setError(null);
      setIsSubmitting(false);
      setIsDeleting(false);
    }
  }, [isOpen, initialItem, defaultsForNewItem]);

  const handleDelete = async (): Promise<void> => {
    if (!initialItem || !token || !projectId) return;
    setIsDeleting(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/projects/${projectId}/timeline-items/${initialItem.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('timeline_delete_failed'));
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('timeline_delete_failed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('timeline_name_required'));
      return;
    }
    if (!token) {
      setError(t('not_authenticated'));
      return;
    }

    const startParsed = startPrecision === 'year' || startPrecision === 'month'
      ? { year: startYear, month: startMonth, day: 1 }
      : parseDateString(startDateStr);
    const startISO = buildTimelineEventIso(
      startPrecision,
      startParsed.year,
      startParsed.month,
      startParsed.day,
      startTimeStr,
      startPrecision === 'second' ? startTimeZone : null
    );

    let endISO: string | null = null;
    let endPrecisionApi: 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | null = null;
    let endTimezoneApi: string | null = null;
    if (hasEnd) {
      const endParsed = endPrecision === 'year' || endPrecision === 'month'
        ? { year: endYear, month: endMonth, day: 1 }
        : parseDateString(endDateStr);
      endISO = buildTimelineEventIso(
        endPrecision,
        endParsed.year,
        endParsed.month,
        endParsed.day,
        endTimeStr,
        endPrecision === 'second' ? endTimeZone : null
      );
      endPrecisionApi = precisionToApiValue(endPrecision);
      endTimezoneApi = endPrecision === 'second' ? endTimeZone : null;
      if (new Date(endISO) < new Date(startISO)) {
        setError(t('timeline_end_before_start'));
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);
      const body = {
      name: name.trim(),
      description: normalizeRichTextDescription(description),
      item_type: itemType,
      tags,
      start_date: startISO,
      start_precision: precisionToApiValue(startPrecision),
      start_timezone: startPrecision === 'second' ? startTimeZone : null,
      end_date: endISO,
      end_precision: endPrecisionApi,
      end_timezone: endTimezoneApi,
    };
    try {
      const url = initialItem
        ? `${API_BASE}/api/projects/${projectId}/timeline-items/${initialItem.id}`
        : `${API_BASE}/api/projects/${projectId}/timeline-items`;
      const response = await fetch(url, {
        method: initialItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.error || (initialItem ? t('timeline_update_failed') : t('timeline_create_failed'))
        );
      }
      const created = initialItem
        ? await response.json().catch(() => ({}))
        : await readJsonWithOnboarding(response);
      onSuccess(created);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : (initialItem ? t('timeline_update_failed') : t('timeline_create_failed'))
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const precisionLabel = (p: PrecisionOption): string => {
    if (p === 'year') return t('timeline_precision_year');
    if (p === 'month') return t('timeline_precision_month');
    if (p === 'day') return t('timeline_precision_day');
    return t('timeline_precision_datetime');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent
        className={`${ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME} sm:max-w-4xl`}
        data-create-entity-from-mention=""
      >
        <DialogTitle className="sr-only">
          {initialItem ? t('edit_event') : t('add_event')}
        </DialogTitle>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <EntityDetailModalLayout
            header={
              initialItem ? (
                <div data-dialog-skip-autofocus>
                  <InlineEditableText
                    value={name}
                    onChange={(next) => {
                      setName(next);
                      setError(null);
                    }}
                    disabled={isSubmitting}
                    placeholder={t('timeline_event_name_placeholder')}
                    skipDialogAutofocus
                    className="text-foreground"
                    inputClassName="h-9 shadow-sm"
                  />
                </div>
              ) : (
                <EntityDetailModalTitle>{t('add_event')}</EntityDetailModalTitle>
              )
            }
            footer={
              <DialogFooter className="flex-row justify-between sm:justify-between">
                <div className="flex gap-2">
                  {initialItem && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => void handleDelete()}
                      disabled={isSubmitting || isDeleting}
                      className="group inline-flex items-center gap-1.5"
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('deleting')}
                        </>
                      ) : (
                        <>
                          <RotatingDeleteIcon />
                          {t('delete')}
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <CancelButton type="button" onClick={onClose} disabled={isSubmitting || isDeleting} />
                  <Button
                    type="submit"
                    disabled={isSubmitting || isDeleting || !name.trim()}
                    dialogPrimary
                    className="inline-flex items-center gap-1.5 bg-[var(--scribane-black)] text-white hover:bg-[var(--scribane-black)] hover:opacity-90"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {initialItem ? t('saving') : t('adding')}
                      </>
                    ) : initialItem ? (
                      <>
                        {t('save')}
                        <DialogPrimaryShortcut className="ml-0.5" />
                      </>
                    ) : (
                      <>
                        {t('add')}
                        <DialogPrimaryShortcut className="ml-0.5" />
                      </>
                    )}
                  </Button>
                </div>
              </DialogFooter>
            }
          >
          <div className="flex flex-col gap-4 p-4">
            {!initialItem && (
              <TooltipProvider>
                <div>
                  <Input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setError(null);
                    }}
                    disabled={isSubmitting}
                    placeholder={t('timeline_event_name_placeholder')}
                    className="text-base font-semibold"
                    autoFocus
                  />
                </div>
              </TooltipProvider>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">{t('timeline_item_type')}</label>
                    <ButtonTooltip tooltip={t('timeline_item_type_knowledge_tooltip')}>
                      <span className="text-xs text-muted-foreground cursor-help">?</span>
                    </ButtonTooltip>
                  </div>
                  <Select
                    value={itemType}
                    onValueChange={(v) => setItemType(v as TimelineItemTypeValue)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('timeline_item_type_placeholder')} />
                    </SelectTrigger>
                    <SelectContent className="z-[2010]">
                      {TIMELINE_ITEM_TYPE_VALUES.map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`timeline_item_type_${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex-1 min-h-0">
                  <label className="text-sm font-medium" htmlFor="timeline-event-description">
                    {t('Description')}
                  </label>
                  <RichTextField
                    id="timeline-event-description"
                    value={description}
                    onChange={setDescription}
                    placeholder={t('timeline_event_description_placeholder')}
                    minHeight="180px"
                    maxHeight="40vh"
                    mentionableEntities={mentionableEntities}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <ProjectTagsField
                  projectId={projectId}
                  tags={tags}
                  onTagsChange={setTags}
                  disabled={isSubmitting}
                  contentClassName="z-[2010]"
                />
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('Start')}</label>
              <Select
                value={startPrecision}
                onValueChange={(v) => {
                  const next = v as PrecisionOption;
                  setStartPrecision(next);
                  if (next === 'second' && !precisionIncludesTime(startPrecision)) {
                    setStartTimeZone(getUserTimeZone());
                  }
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('timeline_precision_placeholder')} />
                </SelectTrigger>
                <SelectContent className="z-[2010]">
                  {(['year', 'month', 'day', 'second'] as const).map((p) => (
                    <SelectItem key={p} value={p}>
                      {precisionLabel(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {startPrecision === 'year' && (
                <TimelineYearInput
                  value={startYear}
                  onChange={setStartYear}
                  disabled={isSubmitting}
                />
              )}
              {startPrecision === 'month' && (
                <div className="flex gap-2">
                  <Select
                    value={String(startMonth)}
                    onValueChange={(v) => setStartMonth(Number(v))}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[2010]">
                      {MONTHS.map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {String(m).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <TimelineYearInput
                    value={startYear}
                    onChange={setStartYear}
                    disabled={isSubmitting}
                    className="w-24"
                  />
                </div>
              )}
              {(startPrecision === 'day' || startPrecision === 'second') && (
                <div className="flex flex-col gap-2">
                  <DatePicker
                    value={startDateStr}
                    onChange={setStartDateStr}
                    disabled={isSubmitting}
                  />
                  {startPrecision === 'second' && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={startTimeStr}
                        onChange={(e) => setStartTimeStr(e.target.value)}
                        disabled={isSubmitting}
                        className="w-[8.5rem] shrink-0"
                      />
                      <SingleSelectCombobox
                        options={timeZoneOptions}
                        value={startTimeZone}
                        onValueChange={setStartTimeZone}
                        placeholder={t('timeline_timezone_placeholder')}
                        searchPlaceholder={t('timeline_timezone_search')}
                        emptyMessage={t('timeline_timezone_empty')}
                        disabled={isSubmitting}
                        inputClassName="min-w-0 flex-1"
                        contentClassName="z-[2010] w-72"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="timeline-has-end"
                checked={hasEnd}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setHasEnd(checked);
                  if (checked) {
                    setEndPrecision(startPrecision);
                    setEndYear(startYear);
                    setEndMonth(startMonth);
                    setEndDateStr(startDateStr);
                    setEndTimeStr(startTimeStr);
                    setEndTimeZone(startTimeZone);
                  }
                }}
                disabled={isSubmitting}
                className="rounded border-input"
              />
              <label htmlFor="timeline-has-end" className="text-sm font-medium">
                {t('End')} ({t('optional')})
              </label>
            </div>
            {hasEnd && (
              <div className="space-y-2 pl-4 border-l-2 border-muted">
                <Select
                  value={endPrecision}
                  onValueChange={(v) => {
                    const next = v as PrecisionOption;
                    setEndPrecision(next);
                    if (next === 'second' && !precisionIncludesTime(endPrecision)) {
                      setEndTimeZone(startTimeZone || getUserTimeZone());
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[2010]">
                    {(['year', 'month', 'day', 'second'] as const).map((p) => (
                      <SelectItem key={p} value={p}>
                        {precisionLabel(p)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {endPrecision === 'year' && (
                  <TimelineYearInput
                    value={endYear}
                    onChange={setEndYear}
                    disabled={isSubmitting}
                  />
                )}
                {endPrecision === 'month' && (
                  <div className="flex gap-2">
                    <Select
                      value={String(endMonth)}
                      onValueChange={(v) => setEndMonth(Number(v))}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[2010]">
                        {MONTHS.map((m) => (
                          <SelectItem key={m} value={String(m)}>
                            {String(m).padStart(2, '0')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <TimelineYearInput
                      value={endYear}
                      onChange={setEndYear}
                      disabled={isSubmitting}
                      className="w-24"
                    />
                  </div>
                )}
                {(endPrecision === 'day' || endPrecision === 'second') && (
                  <div className="flex flex-col gap-2">
                    <DatePicker
                      value={endDateStr}
                      onChange={setEndDateStr}
                      disabled={isSubmitting}
                    />
                    {endPrecision === 'second' && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={endTimeStr}
                          onChange={(e) => setEndTimeStr(e.target.value)}
                          disabled={isSubmitting}
                          className="w-[8.5rem] shrink-0"
                        />
                        <SingleSelectCombobox
                          options={timeZoneOptions}
                          value={endTimeZone}
                          onValueChange={setEndTimeZone}
                          placeholder={t('timeline_timezone_placeholder')}
                          searchPlaceholder={t('timeline_timezone_search')}
                          emptyMessage={t('timeline_timezone_empty')}
                          disabled={isSubmitting}
                          inputClassName="min-w-0 flex-1"
                          contentClassName="z-[2010] w-72"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
                <div className="space-y-2" ref={relatedEntitiesSectionRef}>
                  <label className="text-sm font-medium">{t('timeline_linked_entities')}</label>
                  {linkedEntities.linksTo.length > 0 || linkedEntities.referencedIn.length > 0 ? (
                    <div className="space-y-2">
                      {linkedEntities.linksTo.length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">{t('links_to')}</p>
                          <div className="flex flex-wrap gap-2">
                            {linkedEntities.linksTo.map((e) => (
                              <RelatedEntityLink
                                key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                                entity={e}
                                projectId={projectIdForLinks}
                                onRemove={() => handleRemoveLinkedEntity(e)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {linkedEntities.referencedIn.length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">{t('referenced_in')}</p>
                          <div className="flex flex-wrap gap-2">
                            {linkedEntities.referencedIn.map((e) => (
                              <RelatedEntityLink
                                key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                                entity={e}
                                projectId={projectIdForLinks}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">—</p>
                  )}
                </div>
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          </EntityDetailModalLayout>
        </form>
      </DialogContent>
    </Dialog>
  );
};
